import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Shuffle as ShuffleIcon,
  RestartAlt as ClearIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  TouchAppOutlined as TouchIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import { getLotteryTypeConfig } from "../../../../../static/Constants";

const NumberPicker = ({
  capacity = 20,
  pickNumbers = 3,
  selectednum = [],
  lotteryType = 2,
  onNumberToggle,
  onQuickPick,
  onClear,
}) => {
  const isFull = selectednum.length === pickNumbers;
  const numCapacity = Math.max(1, Number(capacity) || 20);
  const numPick = Math.max(1, Number(pickNumbers) || 3);
  const typeConfig = getLotteryTypeConfig(lotteryType);

  // Generate slots array [0, 1, ... pickNumbers - 1]
  const slots = Array.from({ length: numPick }, (_, idx) => selectednum[idx]);

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.lg,
        border: `1px solid ${colors.slate[200]}`,
        boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
        bgcolor: "#ffffff",
        overflow: "visible",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Header Section */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: colors.slate[900], display: "flex", alignItems: "center", gap: 1 }}
            >
              <TouchIcon sx={{ color: typeConfig.color }} />
              Select Your Lucky Numbers
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.25 }}>
              Choose exactly{" "}
              <strong style={{ color: typeConfig.color }}>{numPick}</strong> numbers
              from <strong>1</strong> to <strong>{numCapacity}</strong>
            </Typography>
          </Box>

          {/* Action Buttons (Quick Pick & Clear) */}
          <Stack direction="row" spacing={1}>
            <CommonButton
              variant="outline"
              size="small"
              startIcon={<ShuffleIcon fontSize="small" />}
              onClick={onQuickPick}
              label="Quick Pick"
              sx={{
                color: typeConfig.color,
                borderColor: typeConfig.borderColor || `${typeConfig.color}40`,
                "&:hover": {
                  bgcolor: typeConfig.bgColor,
                  borderColor: typeConfig.color,
                  color: typeConfig.color,
                },
              }}
            />
            <CommonButton
              variant="clearAction"
              size="small"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={onClear}
              disabled={selectednum.length === 0}
              label="Clear"
            />
          </Stack>
        </Stack>

        {/* Selected Numbers Rack / Tray */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: colors.slate[700] }}
            >
              Selected Ticket Numbers:
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isFull ? (
                <CheckIcon sx={{ fontSize: 16, color: typeConfig.color }} />
              ) : null}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: isFull ? typeConfig.color : colors.slate[500],
                }}
              >
                {selectednum.length} / {numPick} Picked
              </Typography>
            </Stack>
          </Stack>

          <Box className="selected-slots-tray">
            {slots.map((val, idx) =>
              val !== undefined ? (
                <Tooltip title="Click to remove" key={idx}>
                  <Box
                    className="slot-ball-filled"
                    onClick={() => onNumberToggle(val)}
                    sx={{
                      background: `${typeConfig.gradient} !important`,
                      boxShadow: `${typeConfig.boxShadow || "0 4px 12px rgba(0,0,0,0.2)"} !important`,
                    }}
                  >
                    <span className="number-label" style={{ color: "#ffffff" }}>
                      {val}
                    </span>
                    <CloseIcon className="remove-icon" sx={{ color: "#ffffff" }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={`Slot ${idx + 1}`} key={idx}>
                  <Box className="slot-ball-empty">
                    <span>?</span>
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: colors.slate[100] }} />

        {/* Number Balls Grid */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: colors.slate[400],
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 1.5,
              display: "block",
            }}
          >
            Number Pool (1 - {numCapacity})
          </Typography>

          <div className="buylottery-numbers-grid">
            {Array.from({ length: numCapacity }, (_, i) => {
              const num = i + 1;
              const isSelected = selectednum.includes(num);

              return (
                <div
                  key={num}
                  className={`lottery-number-ball ${isSelected ? "selected" : ""}`}
                  style={
                    isSelected
                      ? {
                          background: typeConfig.gradient,
                          borderColor: typeConfig.color,
                          color: "#ffffff",
                          boxShadow: typeConfig.boxShadow,
                        }
                      : {}
                  }
                  onClick={() => onNumberToggle(num)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onNumberToggle(num);
                    }
                  }}
                >
                  {num}
                </div>
              );
            })}
          </div>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NumberPicker;
